<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(defineProps<{
  imageUrl: string;
  imageAlt: string;
  categoryLabel?: string;
  additionalImageCount?: number;
  loading?: 'eager' | 'lazy';
  displayWidthPx?: number;
}>(), {
  categoryLabel: '',
  additionalImageCount: 0,
  loading: 'lazy',
});

const frameStyle = computed(() => {
  if (typeof props.displayWidthPx !== 'number' || !Number.isFinite(props.displayWidthPx) || props.displayWidthPx <= 0) {
    return undefined;
  }

  return { width: `${props.displayWidthPx}px` };
});

const emit = defineEmits<{
  activate: [];
  load: [event: Event];
  error: [event: Event];
}>();
</script>

<template>
  <button
    type="button"
    class="listing-creative-trigger"
    :aria-label="`Enlarge ${imageAlt}`"
    @click="emit('activate')"
  >
    <span v-if="categoryLabel" class="category-marker">
      {{ categoryLabel }}
    </span>

    <span class="listing-creative-frame" :style="frameStyle">
      <img
        :key="imageUrl"
        :src="imageUrl"
        :alt="imageAlt"
        :loading="loading"
        class="listing-creative-image"
        @load="emit('load', $event)"
        @error="emit('error', $event)"
      />
      <span class="listing-enlarge-overlay" aria-hidden="true">
        <span class="listing-enlarge-label listing-enlarge-label--pointer">Click to enlarge</span>
        <span class="listing-enlarge-label listing-enlarge-label--touch">Tap to enlarge</span>
      </span>
      <span v-if="additionalImageCount > 0" class="listing-image-count" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
        +{{ additionalImageCount }}
      </span>
    </span>
  </button>
</template>

<style scoped>
.listing-creative-trigger {
  display: block;
  width: 100%;
  padding: 0;
  overflow: hidden;
  color: inherit;
  text-align: inherit;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.listing-creative-trigger:focus-visible {
  outline: 3px solid var(--accent-primary);
  outline-offset: -3px;
}

.category-marker {
  position: relative;
  z-index: 2;
  display: block;
  width: fit-content;
  max-width: calc(100% - var(--spacing-4));
  padding: 0.3rem 0.65rem;
  overflow: hidden;
  color: var(--accent-fill-text-color);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: var(--accent-primary);
  border-right: 1px solid var(--accent-primary);
  border-bottom: 1px solid var(--accent-primary);
  border-bottom-right-radius: var(--radius-sm);
}

.listing-creative-frame {
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow: hidden;
  background: var(--bg-tertiary);
}

.listing-creative-image {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  image-orientation: from-image;
  transition: transform 0.3s ease;
}

.listing-enlarge-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0);
  pointer-events: none;
  transition: background 0.3s ease;
}

.listing-enlarge-label {
  padding: 8px 16px;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.68);
  border-radius: var(--radius-md);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.listing-enlarge-label--touch {
  display: none;
}

.listing-image-count {
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.75);
  border-radius: var(--radius-full);
  backdrop-filter: blur(4px);
}

.listing-creative-trigger:focus-visible .listing-enlarge-overlay {
  background: rgba(0, 0, 0, 0.3);
}

.listing-creative-trigger:focus-visible .listing-enlarge-label {
  opacity: 1;
}

@media (hover: hover) and (pointer: fine) {
  .listing-creative-trigger:hover .listing-creative-image {
    transform: scale(1.02);
  }

  .listing-creative-trigger:hover .listing-enlarge-overlay {
    background: rgba(0, 0, 0, 0.3);
  }

  .listing-creative-trigger:hover .listing-enlarge-label--pointer {
    opacity: 1;
  }
}

@media (hover: none), (pointer: coarse) {
  .listing-enlarge-overlay {
    align-items: flex-end;
    justify-content: flex-end;
    padding: 8px;
  }

  .listing-enlarge-label--pointer {
    display: none;
  }

  .listing-enlarge-label--touch {
    display: inline-block;
    padding: 6px 10px;
    font-size: 0.75rem;
    opacity: 1;
  }

  .listing-image-count {
    right: auto;
    left: 8px;
    bottom: 8px;
  }
}
</style>
